/* -------------------------------------------------------------------------- */
/*  SCBI – Sage-300 TXT export                                                */
/* -------------------------------------------------------------------------- *
 *  - "*" header record provides project import                                *
 *  - Line items split into LMSEO buckets, each a flat "C" cost record         *
 *  - Cost code from the ACCT Code sort field; markup cost code resolved from   *
 *    the markup name via a keyword map (updated per client list, May-2024),    *
 *    category fixed to "GE"                                                    *
 *  - Returns [stringLines, "txt"]                                             */
/* -------------------------------------------------------------------------- */

import { CategoryMap } from "./categories";
import { BudgetImportConfig } from "./framework/types";

/* ───────────────────────── 1. Cost-code map ─────────────────────────────── */

const MARKUP_CODES: Record<string, string> = {
  /* 01 – General Conditions / Requirements */
  generalconditions: "01-000-000",
  generalrequirements: "01-500-000",

  /* 99-100-1xx – Insurance */
  insurance: "99-100-100",
  specialprojectinsurance: "99-100-110",
  subdefaultinsurance: "99-120-000",

  /* 99-100-2xx – Design */
  architecturaldesigncosts: "99-100-200",
  structuralengineeringdesign: "99-100-210",
  mepengineeringdesigncosts: "99-100-220",
  engineeringdesigncosts: "99-100-220", // alias
  civilengineeringdesigncosts: "99-100-230",

  /* 99-100-3xx – Permits */
  permitallowance: "99-100-300",

  /* 99-100-4xx – Bonds */
  bonds: "99-100-400",
  bidbond: "99-100-410",
  paymentbonds: "99-410-000",
  performancebonds: "99-420-000",

  /* 99-100-5xx – Contingency */
  estimatecontingency: "99-100-500",
  designcontingency: "99-100-510",
  constructioncontingency: "99-100-520",
  contingency: "99-500-000", // generic / legacy

  /* 99-100-6xx – Allowances */
  allowances: "99-100-600",

  /* 99-100-7xx – Consultants & Testing */
  consultants: "99-100-700",
  specialinspections: "99-100-710",

  /* 99-990 – Fee */
  fee: "99-990-999",
};

const CATEGORY_MAP: CategoryMap = {
  labor: "LA",
  material: "MA",
  equip: "EQ",
  sub: "ST",
  other: "GE",
  total: "GE",
};

/* ───────────────────────── 2. Helpers ───────────────────────────────────── */

const resolveCostCode = (raw: string): string =>
  Object.entries(MARKUP_CODES).find(([k]) => raw.toLowerCase().replace(/\s+/g, "").includes(k))?.[1] ?? "99-000-000";

/* ───────────────────────── 3. Config ────────────────────────────────────── */

export const scbiConfig: BudgetImportConfig = {
  format: "sage",
  requires: { floorAreas: true, project: true },
  sage: {
    ext: "txt",
    categoryMap: CATEGORY_MAP,
    buildHeader: ({ project, totalArea }) => {
      const addr = (project!.address ?? "").split(", ");
      const addr1 = addr[0] ?? "";
      const city = addr[1] ?? "";
      const [state = "", zip = ""] = (addr[2] ?? "").split(" ");
      return [
        '"*"',
        '"99999"', // record type, job #
        `"${project!.name}"`, // description
        `"${addr1}"`,
        `"${city}"`,
        `"${state}"`,
        `"${zip}"`,
        '""',
        '""',
        '""',
        '""',
        '""', // unused Sage fields
        totalArea,
        '"sf"',
      ].join(",");
    },
    // Column 7 of a "C" record carries the TOTAL amount for that line-item split (signed when
    // Qty < 0), not the unit cost.
    renderLineItem: ({ row, category, unitCost, ctx }) => {
      const amount = row.quantity * unitCost;
      return [
        [
          '"C"',
          // Coerce null/undefined to "": the migrated data source returns null for an unset field
          // (e.g. a line item with no ACCT Code sort assignment, or no uom) where the legacy source
          // returned "", which would otherwise render as the literal text "null" in the export.
          `"${row["ACCT Code_code"] ?? ""}"`,
          '""',
          `"${category}"`,
          ctx.dateNum,
          row.quantity,
          `"${row.uom ?? ""}"`,
          amount.toFixed(2),
        ].join(","),
      ];
    },
    renderMarkup: ({ row, ctx }) => [
      [
        '"C"',
        `"${resolveCostCode(row.name)}"`,
        '""',
        '"GE"', // GE in lieu of MU
        ctx.dateNum,
        '""',
        '""',
        Number(row.total).toFixed(2),
      ].join(","),
    ],
  },
};
